import React from 'react';

export interface OtbSummaryPillsProps {
  planSales: number | null | undefined;
  planInflow: number | null | undefined;
  onOrder: number | null | undefined;
  otb: number | null | undefined;
  priceBasis?: 'retail' | 'cost';
  // 'stacked': two pills per line under a group name. 'inline': one wrapping line (total row).
  layout?: 'stacked' | 'inline';
  otbLabel?: string;
  className?: string;
}

// Whole dollars with a leading minus, e.g. -$5,041 -- plan figures never show cents.
export const formatPillCurrency = (value: number | null | undefined): string => {
  if (value == null || Number.isNaN(value)) return '$—';
  const rounded = Math.round(Math.abs(value)).toLocaleString('en-US', { maximumFractionDigits: 0 });
  return `${value < 0 ? '-$' : '$'}${rounded}`;
};

// Hover text on every set of pills: what plan / OTB figures are scoped by (SPS-5667 #13).
export const OTB_PILLS_SCOPE_NOTE =
  'Plan and OTB follow the location, class and category filters (including Exclude selected instead). They are not split by vendor: plan data has no vendor dimension, so a vendor filter does not change them.';

const pillCss = 'inline-flex items-center gap-1.5 rounded-[7px] bg-white border border-[#E4E4E7] whitespace-nowrap shadow-2xs';

const Pill: React.FC<{ label: string; value: number | null | undefined; tone?: 'neutral' | 'otb'; size: string }> = ({
  label,
  value,
  tone = 'neutral',
  size,
}) => (
  <div className={`${pillCss} ${size}`} data-testid="otb-pill">
    <span className={`font-medium ${tone === 'otb' ? 'text-[#6D28D9]' : 'text-[#667085]'}`}>{label}:</span>
    <span
      className={`font-mono font-bold ${
        tone === 'otb' ? ((value ?? 0) < 0 ? 'text-[#D92D20]' : 'text-[#7C3AED]') : 'text-[#101828]'
      }`}
    >
      {formatPillCurrency(value)}
    </span>
  </div>
);

/**
 * Plan Sales, Planned Inflow, On Order and OTB pills, shared by Vendor Performance and Top
 * Sellers group rows and their "Total Store Plan & OTB" row. White cards; OTB is purple, or red
 * when negative. Renders nothing when there is no plan figure at all, so a group without plan
 * data shows no pills rather than $0 ones.
 */
export const OtbSummaryPills: React.FC<OtbSummaryPillsProps> = ({
  planSales,
  planInflow,
  onOrder,
  otb,
  priceBasis = 'retail',
  layout = 'stacked',
  otbLabel = 'OTB',
  className = '',
}) => {
  if ([planSales, planInflow, onOrder, otb].every((value) => value == null)) return null;

  const isCost = priceBasis === 'cost';
  const size = layout === 'inline' ? 'px-2.5 py-1 text-[12px]' : 'px-2.5 py-0.5 text-[11px]';
  const pills = {
    planSales: <Pill label="Plan Sales" value={planSales} size={size} />,
    planInflow: <Pill label="Planned Inflow" value={planInflow} size={size} />,
    onOrder: <Pill label={`On Order (${isCost ? 'Cost' : 'Retail'})`} value={onOrder} size={size} />,
    otb: <Pill label={`${otbLabel}${isCost ? ' (Cost)' : ''}`} value={otb} tone="otb" size={size} />,
  };

  if (layout === 'inline') {
    return (
      <div className={`flex items-center gap-2.5 font-sans flex-wrap ${className}`} data-testid="otb-pills" title={OTB_PILLS_SCOPE_NOTE}>
        {pills.planSales}
        {pills.planInflow}
        {pills.onOrder}
        {pills.otb}
      </div>
    );
  }

  return (
    <div className={`flex flex-col gap-1 font-sans ${className}`} data-testid="otb-pills" title={OTB_PILLS_SCOPE_NOTE}>
      <div className="flex items-center gap-1.5 flex-nowrap whitespace-nowrap">
        {pills.planSales}
        {pills.planInflow}
      </div>
      <div className="flex items-center gap-1.5 flex-nowrap whitespace-nowrap">
        {pills.onOrder}
        {pills.otb}
      </div>
    </div>
  );
};

export default OtbSummaryPills;
